import React, { useState } from "react";
import { motion } from "framer-motion";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { api } from "@/lib/api";
import { GraduationCap, Train, Stethoscope, Trees, TrendingUp, ShoppingBag, Baby, Briefcase } from "lucide-react";

const SIM_MAP = "https://static.prod-images.emergentagent.com/jobs/9328c3b4-4557-4408-9bb0-88391ddd609d/images/eae5737a8e75ccd52da3fff306dd55e565b535b960402d28b5f9d4b00b317b18.jpeg";



// key -> approximate spot on the isometric map (percent of container)
const NEEDS = [
  { key: "growth",    label: "Growth corridor",     icon: TrendingUp,   pos: { top: "21%", left: "46%" } },
  { key: "schools",   label: "Good schools",        icon: GraduationCap, pos: { top: "34%", left: "30%" } },
  { key: "childcare", label: "Childcare",           icon: Baby,          pos: { top: "41%", left: "22%" } },
  { key: "transport", label: "Transport links",     icon: Train,        pos: { top: "60%", left: "18%" } },
  { key: "work",      label: "Close to work",       icon: Briefcase,    pos: { top: "47%", left: "33%" } },
  { key: "shops",     label: "Shops & dining",      icon: ShoppingBag,  pos: { top: "46%", left: "46%" } },
  { key: "parks",     label: "Parks & nature",      icon: Trees,        pos: { top: "31%", left: "66%" } },
  { key: "medical",   label: "Medical & hospitals", icon: Stethoscope,  pos: { top: "30%", left: "85%" } },
];

export default function Location() {
  const { session, data, next, back, mergeData } = useJourney();
  const existing = data.location?.needs || [];
  const [selected, setSelected] = useState(existing);
  const [notes, setNotes] = useState(data.location?.notes || "");

  const toggle = (k) => setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const proceed = async () => {
    const payload = { needs: selected, notes };
    mergeData({ location: payload });
    api.patchSession(session.id, { data: { location: payload } }).catch(() => {});
    next();
  };

  return (
    <StageFrame testId="stage-location" className="px-6 md:px-14 pt-16 pb-28">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
        {/* Left: intro + compact cards + notes */}
        <div>
          <Eyebrow>Location & Lifestyle</Eyebrow>
          <h1 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-forest mt-3">
            Let's start with what needs to be close.
          </h1>
          <p className="text-mutedText mt-3 max-w-lg text-sm">
            Not a suburb — a way of living. Tap what matters <span className="text-forest">in order of priority</span> — 1 is most important. Watch your neighbourhood build itself.
          </p>

          <div className="grid grid-cols-2 gap-2.5 mt-7">
            {NEEDS.map(({ key, label, icon: Icon }) => {
              const rank = selected.indexOf(key);
              const on = rank !== -1;
              return (
                <button
                  key={key}
                  data-testid={`location-need-${key}`}
                  onClick={() => toggle(key)}
                  className={`group relative flex items-center gap-3 rounded-xl border p-3.5 text-left transition-all hover:-translate-y-0.5 ${
                    on ? "border-brand bg-brand/5 shadow-lg shadow-brand/10" : "border-line bg-white/5"
                  }`}
                >
                  <Icon size={18} className={on ? "text-brand shrink-0" : "text-mutedText shrink-0"} />
                  <span className={`text-sm font-medium leading-tight ${on ? "text-forest" : "text-forest/80"}`}>{label}</span>
                  {on && (
                    <span
                      data-testid={`location-rank-${key}`}
                      className="ml-auto grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand text-xs font-semibold text-white shadow-md shadow-brand/30"
                    >
                      {rank + 1}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-6">
            <label className="label-eyebrow text-mutedText">Anything specific?</label>
            <textarea
              data-testid="location-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. within 15 min of Mum, near the coast, close to the airport…"
              className="mt-2 w-full rounded-xl border border-line bg-white/5 p-4 text-forest focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
        </div>

        {/* Right: SimCity-style map with priority pins */}
        <motion.figure
          data-testid="location-sim-map"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="relative overflow-hidden rounded-2xl border border-line bg-surface"
        >
          <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }} className="relative">
            <img src={SIM_MAP} alt="Your neighbourhood map" className="w-full object-cover" />

            {NEEDS.map(({ key, label, icon: Icon, pos }) => {
              const rank = selected.indexOf(key);
              const on = rank !== -1;
              return (
                <button
                  key={key}
                  data-testid={`map-pin-${key}`}
                  onClick={() => toggle(key)}
                  className="absolute -translate-x-1/2 -translate-y-1/2 focus:outline-none"
                  style={{ top: pos.top, left: pos.left }}
                  title={label}
                >
                  {on ? (
                    <motion.span
                      initial={{ scale: 0, y: -6 }}
                      animate={{ scale: 1, y: 0 }}
                      transition={{ type: "spring", stiffness: 300, damping: 16 }}
                      className="relative flex flex-col items-center"
                    >
                      <span className="absolute -top-1 h-9 w-9 animate-ping rounded-full bg-brand/40" />
                      <span className="relative grid h-9 w-9 place-items-center rounded-full bg-brand text-sm font-bold text-white shadow-lg shadow-brand/40 ring-2 ring-white/70">
                        {rank + 1}
                      </span>
                      <span className="mt-1 whitespace-nowrap rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                        {label}
                      </span>
                    </motion.span>
                  ) : (
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-black/50 text-white/80 ring-1 ring-white/30 backdrop-blur-sm transition hover:bg-black/70 hover:text-white">
                      <Icon size={15} />
                    </span>
                  )}
                </button>
              );
            })}
          </motion.div>
          <figcaption className="px-4 py-3 text-xs italic text-mutedText">
            Your neighbourhood, ranked — tap the map or a card to pin what matters most.
          </figcaption>
        </motion.figure>
      </div>

      <ConsultantNav testId="stage-location" onBack={back} onNext={proceed} nextLabel="Now let's talk numbers" />
    </StageFrame>
  );
}
